/* eslint-disable no-param-reassign */
import React, { useState, useEffect } from 'react';
import { get, sortBy } from 'lodash';
import TagManager from 'react-gtm-module';
import { FlexInset, PageTitleHelmet } from '@/components';
import shortid from 'shortid';
import emailIsValidish from '@/utils/formUtils';
import { 
  APP_ROUTES, 
  DEFAULT_ERROR_MESSAGE, 
  EMAIL_REQUIRED_MESSAGE, 
  GRADE_REQUIRED_MESSAGE, 
  SCHOOL_NAME_REQUIRED_MESSAGE,
  EMAIL_STRUCTURE_ERROR,
} from '@/config/constants';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import ParentSignUp from './ParentSignUp';
import {
  SCHOOL_FIELD_TYPE,
  GRADE_FIELD_TYPE,
  SCHOOL_NAME_TOOLTIP,
  convertToDescriptionApi,
} from './SignUp.constants';
import { ErrorCodes } from './ErrorCodes';
import { SEARCH_FOR_MY_SCHOOL } from '@/graphql/queries';
import { SUBMIT_PARENT_REGISTRATION } from '@/graphql/mutation';
import { AcademicGroupsResponse, RegisterFamilyResponse } from '@/graphql/types';
import { ApolloClientName } from '@/config/apolloClient';
import { useSnackbar } from '@/design-system';

export type EmailState = {
  value: string;
  error: boolean;
  errorText: string;
  userAlreadyExists: boolean;
};

export type Enrollment = {
  institutionType: string;
  institutionId: number;
  hierarchyType: string;
  hierarchyId: number;
  gradeLevel: string;
  schoolYear: string;
};

export type Grade = {
  id: string;
  description: string;
};

export type School = {
  schoolId: number;
  schoolName: string;
  districtId: number;
  districtName: string;
  gradeLevels: string[];
  description: string;
};

export type Field = {
  id: string;
  renderId: number;
  value: Grade | School | string | null;
  grades: Grade[] | null;
  renderGradesInput: boolean | null;
  dirty: boolean;
  error: boolean;
  errorText: string;
  hasToolTip: boolean;
  toolTipText: string;
  type: string;
};

export type FormSection = {
  label: string;
  id: string;
  renderId: number;
  fields: Field[];
};

const initialFormState = [
  {
    label: 'Child',
    id: shortid.generate(),
    renderId: 1,
    fields: [
      {
        id: shortid.generate(),
        renderId: 1,
        value: null,
        grades: null,
        renderGradesInput: null,
        error: false,
        dirty: false,
        errorText: '',
        hasToolTip: true,
        toolTipText: SCHOOL_NAME_TOOLTIP,
        type: SCHOOL_FIELD_TYPE,
      },
      {
        id: shortid.generate(),
        renderId: 2,
        value: null,
        grades: null,
        renderGradesInput: false,
        error: false,
        dirty: false,
        errorText: '',
        hasToolTip: false,
        toolTipText: '',
        type: GRADE_FIELD_TYPE,
      },
    ],
  },
];

export const getSchoolYear = () => {
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;

  return `${currentYear}-${nextYear}`;
};

const requiredMessageError = (fieldType: string): string => {
  if (fieldType === SCHOOL_FIELD_TYPE) {
    return SCHOOL_NAME_REQUIRED_MESSAGE;
  }
  if (fieldType === GRADE_FIELD_TYPE) {
    return GRADE_REQUIRED_MESSAGE;
  }
  return 'This field is required';
};

export const SignUpContainer: React.FunctionComponent = () => {
  const showToast = useSnackbar();
  const [emailState, setEmailState] = useState<EmailState>({
    value: '',
    error: false,
    errorText: EMAIL_STRUCTURE_ERROR,
    userAlreadyExists: false,
  });
  const [formState, setFormState] = useState<FormSection[]>(initialFormState);
  const [formValidState, setFormValidState] = useState<boolean>(false);
  const [getSchools] = useLazyQuery<AcademicGroupsResponse>(SEARCH_FOR_MY_SCHOOL);
  const [parentRegistration] = useMutation<RegisterFamilyResponse>(SUBMIT_PARENT_REGISTRATION, {
    context: {
      clientName: ApolloClientName.AccountsBff,
    },
  });
  const navigate = useNavigate();
  const checkForm = () => {
    const emailValid =
      emailState.value !== '' && emailIsValidish(emailState.value) && !emailState.userAlreadyExists;
    const fieldsValid: boolean[] = [];
    formState.forEach(section => {
      const isSectionValid = section.fields.every(f => f.value !== null);
      fieldsValid.push(isSectionValid);
    });
    setFormValidState(emailValid && fieldsValid.every(f => f === true));
  };

  const handleFieldChange = async (value: any, sectionId: string, fieldId: string) => {
    const gradeLevels: string[] = get(value, 'gradeLevels', null);

    const sectionsToPersist = formState.filter(section => section.id !== sectionId);
    const targetSection = formState.filter(section => section.id === sectionId)[0];
    // @ts-ignore
    const fieldsToPersist = targetSection.fields.filter(field => field.id !== fieldId)[0];
    const fieldToUpdate = targetSection.fields.filter(field => field.id === fieldId)[0];

    const updatedFieldsToPersist = {
      ...fieldsToPersist,
      grades:
        Array.isArray(gradeLevels) && gradeLevels.length > 0
          ? gradeLevels.map(grade => convertToDescriptionApi(grade))
          : null,
      renderGradesInput:
        Array.isArray(gradeLevels) &&
        gradeLevels.length > 0 &&
        fieldsToPersist.type === GRADE_FIELD_TYPE,
      value: fieldsToPersist.type === GRADE_FIELD_TYPE ? null : fieldsToPersist.value,
    };

    const updatedField = {
      ...fieldToUpdate,
      value,
      dirty: true,
      error: fieldToUpdate.dirty && value === null,
      errorText:
        fieldToUpdate.dirty && value === null
          ? requiredMessageError(fieldToUpdate.type)
          : '',
    };

    const updatedSection = {
      ...targetSection,
      fields: sortBy([updatedFieldsToPersist, updatedField], r => r.renderId),
    };

    setFormState(sortBy([...sectionsToPersist, { ...updatedSection }], s => s.renderId));
  };

  const lookUpSchools = async (options, callback) => {
    try {
      const searchTerms = `parentRole-${options.input}` || 'San Diego';
      const { data } = await getSchools({
        variables: {
          searchTerms,
        },
        context: {
          clientName: ApolloClientName.AccountsBff,
        },
      });

      const results = data?.searchAcademicGroups.schools.reduce(
        // @ts-ignore
        (accum, curr) => [...accum, { ...curr, description: curr.schoolName }],
        []
      );

      callback(results);
    } catch (e) {
      // tslint:disable:no-console
      console.log('Unable to fetch school names - ', e);
    }
  };

  const handleEmailChange = (value: string) => {
    setEmailState({
      value,
      error: !emailIsValidish(value),
      userAlreadyExists: false,
      errorText: value.length === 0 ? EMAIL_REQUIRED_MESSAGE : EMAIL_STRUCTURE_ERROR,
    });
  };

  const handleSectionRemoval = (sectionId: string) => {
    const sectionsToPersist = formState.filter(({ id }) => id !== sectionId);

    setFormState([]);

    sectionsToPersist.forEach((section, index) => {
      const childNum = index + 1;

      section.label = 'Child';
      section.renderId = childNum;
    });

    setFormState([...sectionsToPersist]);
  };

  const showError = (msg?: string) => {
    const message = msg || DEFAULT_ERROR_MESSAGE;
    showToast({ message: message });
  };

  const setErrorMessageForEmptyFields = () => {
    if (emailState.value === '') {
      setEmailState({
        ...emailState,
        error: true,
        errorText: EMAIL_REQUIRED_MESSAGE,
      });
    }
    const newFormState = formState.map(section => {
      const fields: Field[] = [];
      section.fields.forEach(field => {
        if (field.value == null) {
          fields.push({
            ...field,
            error: true,
            errorText: requiredMessageError(field.type),
          });
        } else {
          fields.push(field);
        }
      });
      section.fields = fields;
      return section;
    });
    setFormState(newFormState);
  };

  const handleSubmit = async (event: Event) => {
    if (!formValidState) {
      setErrorMessageForEmptyFields();
      return;
    }
    event.preventDefault();

    // prep input for mutation
    const { value: email } = emailState;
    const enrollments = formState.map(section => {
      const schoolField = section.fields.find(field => field.type === SCHOOL_FIELD_TYPE);

      const gradeField = section.fields.find(field => field.type === GRADE_FIELD_TYPE);

      const {
        // @ts-ignore
        value: { schoolId: institutionId, districtId: hierarchyId },
      } = schoolField;

      let gradeLevel = 'N';
      if (gradeField && gradeField.value !== null) {
        const {
          // @ts-ignore
          value: { description },
        } = gradeField;
        gradeLevel = description;
      }

      return {
        schoolType: 'peachjar-school',
        institutionType: 'school',
        institutionId: institutionId.toString(),
        hierarchyType: 'district',
        hierarchyId: hierarchyId.toString(),
        gradeLevel,
        schoolYear: getSchoolYear(),
        status: 'active',
      };
    });

    try {
      const response = await parentRegistration({
        variables: {
          input: {
            email,
            enrollments,
          },
        },
      });

      const successful = response.data?.registerFamilyNewModel;

      if (successful) {
        TagManager.dataLayer({
          dataLayer: {
            event: 'signup',
            accountType: 'parent',
          },
        });

        const encodedEmail = encodeURIComponent(email);
        navigate(`${APP_ROUTES.magicLinkSuccess}?email=${encodedEmail}`);
        return;
      }
      const errors = get(response, 'errors', []);

      if (errors.length > 0) {
        const { code } = errors[0].extensions;
        if (code && code === ErrorCodes.EmailAlreadyAssociatedToParent) {
          setEmailState({
            ...emailState,
            error: true,
            errorText: '',
            userAlreadyExists: true,
          });
        }
      }
    } catch (e) {
      showError();
    }
  };

  const handleAddChild = () => {
    const childNum = formState.length + 1;

    const newSection = {
      label: 'Child',
      id: shortid.generate(),
      renderId: childNum,
      fields: [
        {
          id: shortid.generate(),
          renderId: 1,
          value: null,
          error: false,
          dirty: false,
          errorText: '',
          hasToolTip: true,
          toolTipText: SCHOOL_NAME_TOOLTIP,
          type: SCHOOL_FIELD_TYPE,
        },
        {
          id: shortid.generate(),
          renderId: 2,
          value: null,
          error: false,
          dirty: false,
          errorText: '',
          hasToolTip: false,
          toolTipText: '',
          type: GRADE_FIELD_TYPE,
        },
      ],
    };

    // @ts-ignore
    setFormState([...formState, newSection]);
  };

  useEffect(() => {
    checkForm();
  }, [emailState, formState]);

  return (
    <FlexInset>
      <PageTitleHelmet>Sign Up</PageTitleHelmet>
      <ParentSignUp
        email={emailState}
        formSections={formState}
        lookUpSchools={lookUpSchools}
        handleSubmit={handleSubmit}
        onGradeChange={handleFieldChange}
        onEmailChange={handleEmailChange}
        onSchoolChange={handleFieldChange}
        onAddChild={handleAddChild}
        onRemoveChild={handleSectionRemoval}
      />
    </FlexInset>
  );
};
